/**
 * BowtieFaultTreeView
 *
 * A Bowtie diagram visualization showing:
 * - CENTER: Initiating Event (the "knot")
 * - LEFT: Fault Tree graph (causes flowing leftward)
 * - RIGHT: ESD graph (pivotal events and end states flowing rightward)
 */

import * as joint from '@joint/plus';
import {FaultTreeViewType} from '../../../@types/fault-tree-view-type';
import {BaseFaultTreeView} from './base-fault-tree-view';
import {
    ChildLinksResult, ExpandButtonConfig, FaultTreeBuildOptions, FaultTreeBuildResult, FaultTreeLayoutOptions
} from './fault-tree-view-strategy';
import type {CanvasEnvironment} from '../canvas-environment';
import type {FaultTree} from '../../../@types/faultTree';
import type {FaultTreeNode} from '../../../@types/faultTreeNode';
import type EventSequence from '../../../@types/event-sequence';
// Note: EsdEvent type is incomplete - events from eventSequence.events have additional
// properties (occurrenceCategory, outcome) at runtime that aren't in the type definition.
// Using 'any' to match the EventSequence.events type definition.
import {
    Barrier, BarrierContainer, BaseLink, InitiatingNode, NegativeNode, NeutralNode, Node, PivotalNode, PositiveNode
} from '../../isam-nodes';
import getPedigreeAbbrev from '../../pedigree-utils';
import {allshapes, positionBarriersInContainers} from '../canvas-actions';

// Note: toDendogram is imported lazily in applyEsdPostProcessing to avoid circular dependency

/**
 * Bowtie fault tree view - displays ESD on left, FT on right, with IE in center.
 */
export class BowtieFaultTreeView extends BaseFaultTreeView {
    readonly viewType = FaultTreeViewType.BOWTIE;
    readonly displayName = 'Bowtie';

    // Track node categories for layout
    private esdNodeIds: Set<string> = new Set();
    private ftNodeIds: Set<string> = new Set();
    private centerNodeId: string | null = null;

    /**
     * Get the default layout options for the bowtie view.
     * Uses horizontal layout with custom positioning.
     */
    getLayoutOptions(): FaultTreeLayoutOptions {
        return {
            direction: 'L',      // ESD flows left
            siblingGap: 20,
            parentGap: 60,
        };
    }

    /**
     * Get layout options for FT expansion from an ESD event.
     * In Bowtie view:
     * - For Initiating Event (IE): horizontal layout (direction: 'L') to display FT to the left
     * - For other ESD nodes (Pivotal events): use standard vertical layout (direction: 'B')
     *
     * @param isInitiatingEvent Whether the event being expanded is the Initiating Event
     * @returns Layout options for the expanded fault tree
     */
    override getFtExpansionLayoutOptions(isInitiatingEvent: boolean): FaultTreeLayoutOptions {
        if (isInitiatingEvent) {
            // Bowtie view: IE's FT displays horizontally to the left
            return {
                direction: 'L',
                siblingGap: 70,
                parentGap: 120,
            };
        }
        // Non-IE events use standard vertical layout
        return super.getFtExpansionLayoutOptions(isInitiatingEvent);
    }

    /**
     * Position the FT root node relative to the clicked ESD event.
     * In Bowtie view:
     * - For Initiating Event (IE): position FT to the left of the IE
     * - For other ESD nodes (Pivotal events): use standard positioning below
     *
     * @param ftRoot The root element of the expanded fault tree
     * @param clickedElement The ESD event element that was expanded
     * @param graph The graph containing the ESD
     */
    override positionFtRoot(
        ftRoot: joint.dia.Element,
        clickedElement: joint.dia.Element,
        graph: joint.dia.Graph
    ): void {
        const isInitiatingEvent = clickedElement.get('data')?.type === 'INITIATING';

        if (isInitiatingEvent) {
            // Bowtie view: position FT root to the left of the IE (accounting for FT root width)
            const elementPos = clickedElement.position();
            const targetSize = ftRoot.size();
            ftRoot.position(elementPos.x - targetSize.width - 150, elementPos.y);
        } else {
            // Non-IE events use standard positioning (below the ESD)
            super.positionFtRoot(ftRoot, clickedElement, graph);
        }
    }

    /**
     * Build the ESD graph for the Bowtie view.
     * Uses the Bowtie's buildGraph method with null FT to build ESD only.
     *
     * @param eventSequence The event sequence data
     * @param graph The graph to populate with ESD nodes
     */
    override buildEsdGraph(
        eventSequence: EventSequence,
        graph: joint.dia.Graph
    ): void {
        // Clear the graph first (like Standard view does with resetCells)
        graph.resetCells([]);

        // Use Bowtie's buildGraph with null FT to build ESD only
        const { graph: esdGraph } = this.buildGraph(
            eventSequence,
            null,
            {
                maxDepth: Infinity,
                expandedNodeIds: new Set<number>()
            }
        );

        // Copy cells from the built graph to the target graph
        graph.addCells(esdGraph.getCells(), { dry: true });
    }

    /**
     * Get layout options for the Bowtie ESD graph.
     * Bowtie uses horizontal layout with ESD flowing right from the IE.
     *
     * @returns Layout options for the ESD TreeLayout
     */
    override getEsdLayoutOptions(): FaultTreeLayoutOptions {
        return {
            direction: 'R',
            siblingGap: 40,
            parentGap: 120,
        };
    }

    /**
     * Apply post-processing to the Bowtie ESD graph.
     * Converts to dendogram style like the standard view.
     *
     * @param graph The graph containing the ESD
     * @param source The source/root element of the ESD
     */
    override applyEsdPostProcessing(
        graph: joint.dia.Graph,
        source: joint.dia.Element
    ): void {
        // Lazy import to avoid circular dependency
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { toDendogram } = require('../canvas-actions');
        toDendogram(graph, source);
    }

    // ==================== Bowtie-specific hook implementations ====================

    /**
     * Get the expand button configuration for an element.
     * In Bowtie view:
     * - IE node and IE's FT nodes use left-positioned buttons
     * - BarrierContainers use left-positioned buttons
     * - Embedded barriers (inside containers) use bottom buttons
     * - Other elements use bottom buttons (default)
     */
    getExpandButtonConfig(
        element: joint.dia.Element,
        graph: joint.dia.Graph
    ): ExpandButtonConfig | undefined {
        const nodeClass = element.get('nodeClass');

        // Embedded barriers use bottom button (like Standard view)
        const isEmbeddedBarrier = !!element.get('containerId');
        if (isEmbeddedBarrier) {
            return undefined; // Use default (bottom)
        }

        // Check if this is an IE node
        const isIeNode = element.get('data')?.type === 'INITIATING';

        // Check if this is a BarrierContainer
        const isBarrierContainer = nodeClass === 'BARRIERCONTAINER';

        // Check if this is part of IE's fault tree
        const isIeFtNode = nodeClass === 'FAULTTREENODE' && this.isPartOfIeFaultTree(element, graph);

        // Use left button for IE, IE's FT nodes, and BarrierContainers
        if (isIeNode || isIeFtNode || isBarrierContainer) {
            const gateType = element.attr('gate/gateType');
            const hasGate = gateType && gateType !== 'none';
            return {
                position: 'left',
                hasGate: !!hasGate
            };
        }

        return undefined; // Use default (bottom)
    }

    /**
     * Determine if horizontal anchors should be used for links.
     * In Bowtie view, horizontal anchors are used for IE's fault tree.
     */
    shouldUseHorizontalAnchors(
        canvasEnvironment: CanvasEnvironment,
        _element: joint.dia.Element
    ): boolean {
        const activeTopNode = canvasEnvironment.activeTopNode;
        if (!activeTopNode) return false;

        // Use horizontal anchors if we're expanding the IE's fault tree
        const isInitiatingEvent = activeTopNode.get('data')?.type === 'INITIATING';
        return isInitiatingEvent;
    }

    /**
     * Check if an element should respond to interactions.
     * BarrierContainers are not interactive (no context menu, selection, highlighting).
     */
    isInteractiveElement(element: joint.dia.Element): boolean {
        const nodeClass = element.get('nodeClass');
        return nodeClass !== 'BARRIERCONTAINER';
    }

    /**
     * Create links from a parent to its children during lazy expansion.
     * Groups barrier children into a BarrierContainer.
     */
    createChildLinks(
        parentElement: joint.dia.Element,
        childIds: number[],
        ftNodeById: Map<number, FaultTreeNode>,
        nodeById: Map<string, joint.dia.Cell>,
        graph: joint.dia.Graph,
        useHorizontalAnchors: boolean
    ): ChildLinksResult {
        const links: joint.dia.Cell[] = [];
        const ftNode = parentElement.get('data') as FaultTreeNode;

        // Don't create container if parent is already a barrier
        const parentIsBarrier = ftNode?.nodeClass === 'BARRIER';

        if (!parentIsBarrier && useHorizontalAnchors) {
            // Find barrier children
            const barrierChildIds = childIds.filter(childId => {
                const childFtNode = ftNodeById.get(childId);
                return childFtNode?.nodeClass === 'BARRIER';
            });

            if (barrierChildIds.length > 0) {
                // Create BarrierContainer with adaptive height
                const container = this.createBarrierContainerForExpansion(
                    parentElement,
                    barrierChildIds,
                    ftNodeById,
                    nodeById,
                    graph
                );

                // Link parent -> container
                const linkToContainer = BaseLink.create(parentElement, container, undefined, { useHorizontalAnchors });
                linkToContainer.set('id', 'link-' + parentElement.get('id') + '-container');
                links.push(linkToContainer);

                // Non-barrier children link to container
                const embeddedBarrierIdSet = new Set(barrierChildIds.map(id => 'ft-' + id));
                childIds.forEach(childId => {
                    const nodeId = 'ft-' + childId;
                    if (embeddedBarrierIdSet.has(nodeId)) return;
                    const child = nodeById.get(nodeId);
                    if (child) {
                        const link = BaseLink.create(container, child, undefined, { useHorizontalAnchors });
                        link.set('id', 'link-container-' + nodeId);
                        links.push(link);
                    }
                });

                return { links, container };
            }
        }

        // No barriers or not Bowtie - link directly
        childIds.forEach(childId => {
            const nodeId = 'ft-' + childId;
            const child = nodeById.get(nodeId);
            if (child) {
                const link = BaseLink.create(parentElement, child, undefined, { useHorizontalAnchors });
                link.set('id', 'link-' + parentElement.get('id') + '-' + nodeId);

                // If parent is embedded, mark link for visual repositioning
                const parentContainerId = parentElement.get('containerId');
                if (parentContainerId) {
                    link.set('embeddedSourceContainerId', parentContainerId);
                }

                links.push(link);
            }
        });

        return { links };
    }

    /**
     * Post-layout processing for positioning barriers inside their containers.
     */
    postLayoutProcessing(graph: joint.dia.Graph): void {
        positionBarriersInContainers(graph);
    }

    /**
     * Check if an element is part of the Initiating Event's fault tree.
     * Traverses up the graph to find if the root is an IE.
     */
    isPartOfIeFaultTree(element: joint.dia.Element, graph: joint.dia.Graph): boolean {
        let current = element;
        const visited = new Set<string>();

        while (current) {
            const currentId = current.id as string;
            if (visited.has(currentId)) break;
            visited.add(currentId);

            // If element is embedded in a container, continue from the container
            const containerId = current.get('containerId');
            if (containerId) {
                const container = graph.getCell(containerId) as joint.dia.Element;
                if (container) {
                    current = container;
                    continue;
                }
            }

            const inboundLinks = graph.getConnectedLinks(current, { inbound: true });
            if (inboundLinks.length === 0) break;

            const sourceId = inboundLinks[0].get('source')?.id;
            if (!sourceId) break;

            const parent = graph.getCell(sourceId) as joint.dia.Element;
            if (!parent) break;

            // If we reached an event node (not a FT node or container), check if it's an IE
            const parentNodeClass = parent.get('nodeClass');
            if (parentNodeClass !== 'FAULTTREENODE' && parentNodeClass !== 'BARRIERCONTAINER') {
                return parent.get('data')?.type === 'INITIATING';
            }
            current = parent;
        }
        return false;
    }

    /**
     * Create a BarrierContainer for lazy-loaded barrier children.
     */
    private createBarrierContainerForExpansion(
        parentElement: joint.dia.Element,
        barrierChildIds: number[],
        ftNodeById: Map<number, FaultTreeNode>,
        nodeById: Map<string, joint.dia.Cell>,
        graph: joint.dia.Graph
    ): joint.dia.Element {
        const gateTypes = ['or', 'and', 'xor', 'priority_and', 'inhibit', 'transfer'];
        const baseHeight = 150;
        const gateExtra = 70;
        const buttonExtra = 30;
        const gap = 15;
        const padding = 10;

        // Calculate height for each embedded node
        const nodeHeights = barrierChildIds.map(childId => {
            const childFtNode = ftNodeById.get(childId);
            const nodeType = childFtNode?.type?.toLowerCase();
            const hasGate = nodeType && gateTypes.includes(nodeType);
            return baseHeight + buttonExtra + (hasGate ? gateExtra : 0);
        });

        const totalHeight = padding * 2 +
            nodeHeights.reduce((sum, h) => sum + h, 0) +
            (barrierChildIds.length - 1) * gap;

        const containerWidth = 380;
        const container = new BarrierContainer({ size: { width: containerWidth, height: totalHeight } });
        const containerId = 'barrier-container-' + parentElement.get('id');
        container.set('id', containerId);
        container.set('nodeClass', 'BARRIERCONTAINER');
        container.set('parentNodeId', parentElement.get('id'));

        // Store barrier info on container for positionBarriersInContainers
        const barrierNodeIds = barrierChildIds.map(id => 'ft-' + id);
        container.set('embeddedBarrierIds', barrierNodeIds);
        container.set('nodeHeights', nodeHeights);
        container.set('barrierGap', gap);
        container.set('barrierPadding', padding);

        graph.addCell(container);

        // Mark barrier children with container reference
        barrierChildIds.forEach((barrierId) => {
            const nodeId = 'ft-' + barrierId;
            const barrierElement = nodeById.get(nodeId) as joint.dia.Element;
            if (barrierElement) {
                barrierElement.set('containerId', containerId);
            }
        });

        return container;
    }

    /**
     * Build the bowtie graph combining ESD and FT.
     */
    buildGraph(
        eventSequence: EventSequence,
        faultTree: FaultTree | null,
        options: FaultTreeBuildOptions
    ): FaultTreeBuildResult {
        const graph = new joint.dia.Graph({}, { cellNamespace: allshapes });
        const nodeMap = new Map<string, joint.dia.Element>();

        // Reset tracking sets
        this.esdNodeIds = new Set();
        this.ftNodeIds = new Set();
        this.centerNodeId = null;

        if (!eventSequence || !eventSequence.events) {
            return { graph, rootElement: null, nodeMap };
        }

        // 1. Find initiating event
        const initiatingEvent = eventSequence.events.find(e => e.type === 'INITIATING');
        if (!initiatingEvent) {
            return { graph, rootElement: null, nodeMap };
        }

        // 2. Create center node (Initiating Event)
        const centerNode = this.createEsdNode(initiatingEvent, eventSequence);
        graph.addCell(centerNode);
        nodeMap.set('event-' + initiatingEvent.id, centerNode);
        this.centerNodeId = 'event-' + initiatingEvent.id;

        // 3. Build LEFT side (ESD: pivotal → end events)
        this.buildEsdSide(graph, eventSequence, initiatingEvent, nodeMap);

        // 4. Build RIGHT side (Fault Tree) if available
        if (faultTree && faultTree.faultTreeNodes && faultTree.faultTreeNodes.length > 0) {
            this.buildFtSide(graph, eventSequence, faultTree, options, nodeMap);
        }

        // 5. Return with center node as root (for layout centering)
        return {
            graph,
            rootElement: centerNode,
            nodeMap
        };
    }

    /**
     * Create a custom layout for the bowtie diagram.
     * This applies two separate tree layouts: one for ESD (left) and one for FT (right).
     */
    createLayout(
        graph: joint.dia.Graph,
        options?: Partial<FaultTreeLayoutOptions>
    ): joint.layout.TreeLayout {
        const layoutOptions = { ...this.getLayoutOptions(), ...options };

        // Apply custom bowtie layout before returning
        this.applyBowtieLayout(graph, layoutOptions);

        // Position barriers inside their containers after layout
        positionBarriersInContainers(graph);

        // Return a dummy layout that won't reposition (positions already set)
        return new joint.layout.TreeLayout({
            graph: new joint.dia.Graph(), // Empty graph so layout() does nothing
            direction: 'R',
            siblingGap: layoutOptions.siblingGap,
            parentGap: layoutOptions.parentGap,
        });
    }

    /**
     * Apply the bowtie layout: ESD flows left, FT flows right from center.
     */
    private applyBowtieLayout(graph: joint.dia.Graph, options: FaultTreeLayoutOptions): void {
        const centerNode = this.centerNodeId ? graph.getCell(this.centerNodeId) as joint.dia.Element : null;
        if (!centerNode) return;

        // Create temporary graphs for ESD and FT
        const esdGraph = new joint.dia.Graph({}, { cellNamespace: allshapes });
        const ftGraph = new joint.dia.Graph({}, { cellNamespace: allshapes });

        // Maps to track original ID -> cloned node (clones get new IDs)
        const esdCloneMap = new Map<string, joint.dia.Element>();
        const ftCloneMap = new Map<string, joint.dia.Element>();

        // Clone center node to both graphs as root
        const centerCloneEsd = centerNode.clone() as joint.dia.Element;
        const centerCloneFt = centerNode.clone() as joint.dia.Element;
        esdGraph.addCell(centerCloneEsd);
        ftGraph.addCell(centerCloneFt);
        esdCloneMap.set(this.centerNodeId!, centerCloneEsd);
        ftCloneMap.set(this.centerNodeId!, centerCloneFt);

        // Clone ESD nodes to ESD graph
        this.esdNodeIds.forEach(nodeId => {
            const node = graph.getCell(nodeId) as joint.dia.Element;
            if (node) {
                const clone = node.clone() as joint.dia.Element;
                esdGraph.addCell(clone);
                esdCloneMap.set(nodeId, clone);
            }
        });

        // Clone ESD links (connect clones by looking up in map)
        graph.getLinks().forEach(link => {
            const sourceId = link.get('source')?.id;
            const targetId = link.get('target')?.id;
            if ((this.esdNodeIds.has(sourceId) || sourceId === this.centerNodeId) &&
                (this.esdNodeIds.has(targetId) || targetId === this.centerNodeId)) {
                const sourceClone = esdCloneMap.get(sourceId);
                const targetClone = esdCloneMap.get(targetId);
                if (sourceClone && targetClone) {
                    const linkClone = link.clone();
                    linkClone.set('source', { id: sourceClone.id });
                    linkClone.set('target', { id: targetClone.id });
                    esdGraph.addCell(linkClone);
                }
            }
        });

        // Clone FT nodes to FT graph
        this.ftNodeIds.forEach(nodeId => {
            const node = graph.getCell(nodeId) as joint.dia.Element;
            if (node) {
                const clone = node.clone() as joint.dia.Element;
                ftGraph.addCell(clone);
                ftCloneMap.set(nodeId, clone);
            }
        });

        // Clone FT links (connect clones by looking up in map)
        graph.getLinks().forEach(link => {
            const sourceId = link.get('source')?.id;
            const targetId = link.get('target')?.id;
            if ((this.ftNodeIds.has(sourceId) || sourceId === this.centerNodeId) &&
                (this.ftNodeIds.has(targetId) || targetId === this.centerNodeId)) {
                const sourceClone = ftCloneMap.get(sourceId);
                const targetClone = ftCloneMap.get(targetId);
                if (sourceClone && targetClone) {
                    const linkClone = link.clone();
                    linkClone.set('source', { id: sourceClone.id });
                    linkClone.set('target', { id: targetClone.id });
                    ftGraph.addCell(linkClone);
                }
            }
        });

        // Apply TreeLayout to ESD (direction: R = children flow right)
        if (this.esdNodeIds.size > 0) {
            const esdLayout = new joint.layout.TreeLayout({
                graph: esdGraph,
                direction: 'R',
                siblingGap: options.siblingGap,
                parentGap: options.parentGap,
            });
            esdLayout.layout();
        }

        // Apply TreeLayout to FT (direction: L = children flow left)
        if (this.ftNodeIds.size > 0) {
            const ftLayout = new joint.layout.TreeLayout({
                graph: ftGraph,
                direction: 'L',
                siblingGap: options.siblingGap,
                parentGap: options.parentGap,
            });
            ftLayout.layout();
        }

        // Get positions of center clones after layout
        const esdCenterPos = centerCloneEsd.position();
        const ftCenterPos = centerCloneFt.position();

        // Position center node at origin
        centerNode.position(0, 0);

        // Copy ESD positions (offset so center is at 0,0)
        this.esdNodeIds.forEach(nodeId => {
            const originalNode = graph.getCell(nodeId) as joint.dia.Element;
            const clonedNode = esdCloneMap.get(nodeId);
            if (originalNode && clonedNode) {
                const clonedPos = clonedNode.position();
                originalNode.position(
                    clonedPos.x - esdCenterPos.x,
                    clonedPos.y - esdCenterPos.y
                );
            }
        });

        // Copy FT positions (offset so center is at 0,0)
        this.ftNodeIds.forEach(nodeId => {
            const originalNode = graph.getCell(nodeId) as joint.dia.Element;
            const clonedNode = ftCloneMap.get(nodeId);
            if (originalNode && clonedNode) {
                const clonedPos = clonedNode.position();
                originalNode.position(
                    clonedPos.x - ftCenterPos.x,
                    clonedPos.y - ftCenterPos.y
                );
            }
        });
    }

    /**
     * Build the ESD (right) side of the bowtie.
     */
    private buildEsdSide(
        graph: joint.dia.Graph,
        eventSequence: EventSequence,
        initiatingEvent: any,
        nodeMap: Map<string, joint.dia.Element>
    ): void {
        // Build ESD events (excluding IE which is already in center)
        const esdEvents = eventSequence.events.filter(e => e.type !== 'INITIATING');

        // Create nodes for each ESD event
        esdEvents.forEach(event => {
            const node = this.createEsdNode(event, eventSequence);
            graph.addCell(node);
            const nodeId = 'event-' + event.id;
            nodeMap.set(nodeId, node);
            this.esdNodeIds.add(nodeId);  // Track for layout
        });

        // Create links from parent to child
        esdEvents.forEach(event => {
            const parentId = 'event-' + event.parentId;
            const parent = nodeMap.get(parentId);
            const child = nodeMap.get('event-' + event.id);

            if (parent && child) {
                const link = BaseLink.create(parent, child, event.parentRelationType);
                link.set('id', 'link-' + parentId + '-event-' + event.id);
                graph.addCell(link);
            }
        });
    }

    /**
     * Build the Fault Tree (right) side of the bowtie.
     */
    private buildFtSide(
        graph: joint.dia.Graph,
        eventSequence: EventSequence,
        faultTree: FaultTree,
        options: FaultTreeBuildOptions,
        nodeMap: Map<string, joint.dia.Element>
    ): void {
        const ftNodeById = this.buildNodeLookupMap(faultTree.faultTreeNodes);
        const depthMap = this.calculateNodeDepths(faultTree.rootNodeId, ftNodeById);
        const expandedNodeIds = options.expandedNodeIds || new Set<number>();
        // Default maxDepth to 3 if not specified (same as default lazy load depth)
        const maxDepth = options.maxDepth ?? 3;

        // Clean stale expanded IDs
        this.cleanStaleExpandedIds(expandedNodeIds, ftNodeById);

        const nodesToLoad = this.determineNodesToLoad(
            faultTree.faultTreeNodes,
            ftNodeById,
            depthMap,
            maxDepth,
            expandedNodeIds
        );

        // Create FT nodes
        nodesToLoad.forEach(nodeId => {
            const ftNode = ftNodeById.get(nodeId);
            if (ftNode) {
                const node = this.createFtNode(ftNode, eventSequence, nodesToLoad);
                graph.addCell(node);
                const ftNodeId = 'ft-' + nodeId;
                nodeMap.set(ftNodeId, node);
                this.ftNodeIds.add(ftNodeId);  // Track for layout
            }
        });

        // Track which barrier nodes are embedded in containers (to skip in direct linking)
        const embeddedBarrierIds = new Set<number>();
        // Map parent node ID -> BarrierContainer for that parent's barrier children
        const containerByParent = new Map<number, joint.dia.Element>();

        // Second pass: Create BarrierContainers for parents with barrier children
        // But only if the parent is NOT itself an embeddable node type
        nodesToLoad.forEach(nodeId => {
            const ftNode = ftNodeById.get(nodeId);
            if (!ftNode?.childIds) return;

            // Helper to check if a node should be embedded in a container
            const isEmbeddableNode = (node: FaultTreeNode | undefined): boolean => {
                if (!node) return false;
                return node.nodeClass === 'BARRIER' ||
                       node.nodeClass === 'INDUCED' ||
                       node.isBackbone === true;
            };

            // Skip if parent is an embeddable type - don't group their children
            if (isEmbeddableNode(ftNode)) return;

            // Identify embeddable children that are loaded (barriers, induced, backbone)
            const barrierChildIds = ftNode.childIds.filter(childId => {
                if (!nodesToLoad.has(childId)) return false;
                const child = ftNodeById.get(childId);
                return isEmbeddableNode(child);
            });

            if (barrierChildIds.length > 0) {
                // Create BarrierContainer
                const container = this.createBarrierContainer(
                    barrierChildIds,
                    ftNodeById,
                    nodeMap,
                    graph
                );
                const containerId = 'barrier-container-' + nodeId;
                container.set('id', containerId);
                container.set('nodeClass', 'BARRIERCONTAINER');
                container.set('parentNodeId', nodeId);
                graph.addCell(container);
                nodeMap.set(containerId, container);
                this.ftNodeIds.add(containerId);
                containerByParent.set(nodeId, container);

                // Mark these barriers as embedded and remove from ftNodeIds
                // (they are positioned relative to container, not by layout)
                barrierChildIds.forEach(id => {
                    embeddedBarrierIds.add(id);
                    this.ftNodeIds.delete('ft-' + id);
                });
            }
        });

        // Create FT links with barrier container logic
        nodesToLoad.forEach(nodeId => {
            const ftNode = ftNodeById.get(nodeId);
            if (!ftNode?.childIds) return;

            const parent = nodeMap.get('ft-' + nodeId);
            if (!parent) return;

            // Check if this parent has a barrier container
            const container = containerByParent.get(nodeId);

            if (container) {
                // Link parent -> container
                const linkToContainer = BaseLink.create(parent, container, undefined, { useHorizontalAnchors: true });
                linkToContainer.set('id', 'link-ft-' + nodeId + '-container');
                graph.addCell(linkToContainer);

                // Non-embedded children link to the container
                ftNode.childIds.forEach(childId => {
                    if (!nodesToLoad.has(childId)) return;
                    if (embeddedBarrierIds.has(childId)) return; // Skip embedded nodes (barriers, induced, backbone)

                    const child = nodeMap.get('ft-' + childId);
                    if (child) {
                        const link = BaseLink.create(container, child, undefined, { useHorizontalAnchors: true });
                        link.set('id', 'link-container-' + nodeId + '-ft-' + childId);
                        graph.addCell(link);
                    }
                });
            } else {
                // No barriers - link children directly to parent as before
                ftNode.childIds.forEach(childId => {
                    if (!nodesToLoad.has(childId)) return;

                    const child = nodeMap.get('ft-' + childId);
                    if (child) {
                        const link = BaseLink.create(parent, child, undefined, { useHorizontalAnchors: true });
                        link.set('id', 'link-ft-' + nodeId + '-ft-' + childId);

                        // If parent is embedded, set source anchor to visually start from container
                        const parentContainerId = parent.get('containerId');
                        if (parentContainerId) {
                            link.set('embeddedSourceContainerId', parentContainerId);
                        }

                        graph.addCell(link);
                    }
                });
            }
        });

        // Link FT root to the initiating event (center) with horizontal anchors
        const ftRoot = nodeMap.get('ft-' + faultTree.rootNodeId);
        const initiatingEvent = eventSequence.events.find(e => e.type === 'INITIATING');
        if (ftRoot && initiatingEvent) {
            const centerNode = nodeMap.get('event-' + initiatingEvent.id);
            if (centerNode) {
                const link = BaseLink.create(centerNode, ftRoot, undefined, { useHorizontalAnchors: true });
                link.set('id', 'link-center-to-ft');
                graph.addCell(link);
            }
        }
    }

    /**
     * Create a BarrierContainer that embeds the given barrier nodes.
     * Barriers are stacked vertically inside the container.
     * Height is adaptive based on whether each node has a gate.
     */
    private createBarrierContainer(
        barrierIds: number[],
        ftNodeById: Map<number, FaultTreeNode>,
        nodeMap: Map<string, joint.dia.Element>,
        _graph: joint.dia.Graph
    ): joint.dia.Element {
        const gateTypes = ['or', 'and', 'xor', 'priority_and', 'inhibit', 'transfer'];
        const baseHeight = 150;  // Node body height
        const gateExtra = 70;    // Extra height for gate (40 offset + 30 gate)
        const buttonExtra = 30;  // Extra height for expand button
        const gap = 15;
        const padding = 10;

        // Calculate height for each embedded node
        const nodeHeights: number[] = barrierIds.map(id => {
            const ftNode = ftNodeById.get(id);
            const hasGate = ftNode?.type && gateTypes.includes(ftNode.type.toLowerCase());
            // All nodes get button space; nodes with gates get extra gate space
            return baseHeight + buttonExtra + (hasGate ? gateExtra : 0);
        });

        // Calculate total container height
        const totalHeight = padding * 2 +
            nodeHeights.reduce((sum, h) => sum + h, 0) +
            (barrierIds.length - 1) * gap;

        // Create container with calculated height
        const containerWidth = 380;
        const container = new BarrierContainer({ size: { width: containerWidth, height: totalHeight } });

        // Container starts at origin; layout will reposition it later
        container.position(0, 0);

        // Set nodeClass so the container is properly filtered on ESD node change
        container.set('nodeClass', 'BARRIERCONTAINER');

        // Store barrier IDs and sizing info on container for positionBarriersInContainers
        container.set('embeddedBarrierIds', barrierIds);
        container.set('nodeHeights', nodeHeights);  // Store individual heights
        container.set('barrierGap', gap);
        container.set('barrierPadding', padding);

        // Mark each barrier with its container reference (for expand handling)
        barrierIds.forEach((barrierId) => {
            const barrierElement = nodeMap.get('ft-' + barrierId);
            if (barrierElement) {
                barrierElement.set('containerId', container.id);
            }
        });

        return container;
    }

    /**
     * Create an ESD node (initiating, pivotal, or end event).
     */
    private createEsdNode(event: any, eventSequence: EventSequence): joint.dia.Element {
        const eventUniqueId = event.uniqueId.replace(eventSequence.uniqueId, '');
        const qval = ['INITIATING', 'END', 'INPRECURSOR'].includes(event.type)
            ? event.frequency
            : event.probability;
        const label = ['INITIATING', 'END', 'INPRECURSOR'].includes(event.type) ? 'F' : 'P';
        const pedigree = getPedigreeAbbrev(event.pedigree);
        const qvalLabel = `${label}=${Number.parseFloat(String(qval)).toExponential(2)}`;
        const bias = '';
        const cictt = event.occurrenceCategory ?? '';

        let node: joint.dia.Element;

        if (event.type === 'INITIATING') {
            node = InitiatingNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
        } else if (event.type === 'PIVOTAL') {
            node = PivotalNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
        } else if (event.type === 'END') {
            if (event.outcome === 'POSITIVE') {
                node = PositiveNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
            } else if (event.outcome === 'NEGATIVE') {
                node = NegativeNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
            } else {
                node = NeutralNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
            }
        } else {
            // Default to neutral for unknown types
            node = NeutralNode.create(eventUniqueId, event.name, qvalLabel, pedigree, bias, cictt);
        }

        node.set({
            data: event,
            id: 'event-' + event.id,
            nodeClass: 'EVENTNODE',
            // Set collapsed: true so expand button shows '+' (FT is not expanded initially)
            collapsed: true
        });
        node.attr('body/title', event.name);

        return node;
    }

    /**
     * Create a Fault Tree node.
     */
    private createFtNode(
        ftNode: FaultTreeNode,
        eventSequence: EventSequence,
        nodesToLoad: Set<number>
    ): joint.dia.Element {
        const eventSequenceUniqueId = eventSequence.uniqueId;
        const ftNodeUniqueId = ftNode.uniqueId.replace(eventSequenceUniqueId, '');
        const quantificationMode = eventSequence.quantificationMode;

        // Determine display value based on quantification mode
        let qval: number;
        let label: string;
        if (quantificationMode === 'FREQUENCY') {
            qval = ftNode.frequency;
            label = 'F';
        } else {
            qval = ftNode.probability;
            label = 'P';
        }
        const qvalLabel = `${label}=${Number.parseFloat(String(qval)).toExponential(2)}`;

        const pedigree = getPedigreeAbbrev(ftNode.pedigree);
        const bias = ftNode.bias ? ftNode.bias.toString() : '';
        const cictt = ftNode.occurrenceCategory ?? '';

        // Create appropriate node type
        let node: joint.dia.Element;
        if (ftNode.nodeClass === 'BARRIER') {
            node = Barrier.create(ftNodeUniqueId, ftNode.name, qvalLabel, pedigree, bias, cictt);
        } else {
            node = Node.create(ftNodeUniqueId, ftNode.name, qvalLabel, pedigree, bias, cictt);
        }

        // Set gate type for non-leaf nodes
        const gateType = ftNode.type?.toLowerCase();
        if (gateType && ['or', 'and', 'xor', 'priority_and', 'inhibit', 'transfer'].includes(gateType)) {
            (node as any).gate(gateType);
        }

        // Check if this node has unloaded children
        const hasUnloadedChildren = this.hasUnloadedChildren(ftNode, nodesToLoad);

        node.set({
            data: ftNode,
            id: 'ft-' + ftNode.id,
            nodeClass: 'FAULTTREENODE',
            hasUnloadedChildren,
            collapsed: hasUnloadedChildren
        });
        node.attr('body/title', ftNode.name);

        return node;
    }
}
